import { useCallback, useEffect, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';
import { toast } from 'react-toastify';

import { API_URL } from 'constant';
import { REACT_APP_API_HOST as API_HOST } from 'envs';
import { loginState } from 'global-stores';
import { useNetwork } from 'hooks';

import type {
	IAlert,
	IAlertQueue,
	IFxPair,
	IFxRate,
	IImportBatch,
	IMonitoringRule,
	IAssignee,
	IFincenConnection,
	IFincenSubmission,
	ISarDraft,
	ISarFilerProfileResp,
	ITransaction,
	ITxnStats,
} from './types';

/**
 * The TM data hooks — everything on these two screens comes from
 * `transaction.monitoring.api` through the gateway.
 *
 * WHY POLLING AND NOT A SOCKET. The tape is described as live, and the honest
 * way to say what this does is: it re-reads on an interval. The panel holds one
 * socket already (the RTDB connection) and it is not this service's; adding a
 * second transport for a screen that reads two endpoints would be a lot of
 * machinery for a refresh a human cannot see the difference in. If the volume
 * ever justifies it, the swap is inside this file.
 *
 * The interval is paused while the tab is hidden. A background tab polling every
 * few seconds all afternoon is load nobody asked for, and it is also how a
 * "live" number ends up hours stale the moment you look at it — the catch-up
 * fetch on `visibilitychange` is what stops that.
 */
const POLL_MS = 6000;

/** How many imports the log shows per page, and how fast a running one refreshes. */
export const IMPORTS_PAGE_SIZE = 20;
const IMPORT_POLL_MS = 2500;

/**
 * The largest file one import accepts. 100 MB.
 *
 * Checked HERE, before a byte is sent, because the alternative is a client
 * watching a 400 MB export upload for ten minutes to be told no at the end. The
 * service enforces the same number (`MAX_UPLOAD_BYTES`) since a client can post
 * directly, and the gateway carries a higher ceiling because JSON escaping
 * inflates the body on the wire.
 */
export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

/** Live upload state, for the progress bar. */
export interface IUploadProgress {
	/** `reading` the file off disk, `uploading` it, then the import takes over. */
	phase: 'idle' | 'reading' | 'uploading' | 'done';
	loaded: number;
	total: number;
	/** Bytes per second, smoothed — see the note in `upload`. */
	bytesPerSec: number;
	/** Seconds left, or null before there is enough data to say. */
	etaSeconds: number | null;
}

const useVisiblePoll = (fn: () => void, ms = POLL_MS) => {
	/* The callback changes identity on every render (it closes over filters), so
	   the interval reads it out of a ref instead of being torn down and rebuilt
	   each time — which would reset the clock and, with a fast enough render
	   loop, mean it never actually fires. */
	const saved = useRef(fn);
	saved.current = fn;

	useEffect(() => {
		let timer: ReturnType<typeof setInterval> | null = null;
		const stop = () => {
			if (timer) clearInterval(timer);
			timer = null;
		};
		const startPolling = () => {
			stop();
			timer = setInterval(() => saved.current(), ms);
		};
		const onVisibility = () => {
			if (document.hidden) stop();
			else {
				saved.current();
				startPolling();
			}
		};
		if (!document.hidden) startPolling();
		document.addEventListener('visibilitychange', onVisibility);
		return () => {
			stop();
			document.removeEventListener('visibilitychange', onVisibility);
		};
	}, [ms]);
};

export interface IQueueFilters {
	tab: string;
	severity?: string;
	bundle?: string;
	q?: string;
	sort?: string;
	pageNo?: number;
	pageSize?: number;
}

/** The triage queue plus its tab badges. */
export const useAlertQueue = (filters: IQueueFilters) => {
	const { get } = useNetwork({ updateState: false });
	const [queue, setQueue] = useState<IAlertQueue | null>(null);
	const [loading, setLoading] = useState(true);
	const [failed, setFailed] = useState(false);

	const load = useCallback(async () => {
		const qs = new URLSearchParams();
		Object.entries(filters).forEach(([k, v]) => {
			if (v) qs.set(k, String(v));
		});
		const resp = await get(`${API_URL.TM_ALERTS}?${qs.toString()}`).catch(
			() => null
		);
		/* A failed poll leaves the LAST good page on screen rather than blanking
		   it: an analyst mid-triage should not lose the queue because one refresh
		   timed out. The strip says so, so a stale page is never silently stale. */
		if (resp?.data) {
			setQueue(resp.data);
			setFailed(false);
		} else {
			setFailed(true);
		}
		setLoading(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(filters)]);

	useEffect(() => {
		load();
	}, [load]);
	useVisiblePoll(load);

	return { queue, loading, failed, reload: load };
};

/**
 * The header: tiles, bins, hour meter and rules-firing, in one request.
 *
 * Reports its own staleness for the same reason the queue does. Every number on
 * this header is labelled "last 60 min", and on a failed poll the last good one
 * stays on screen — so an hour-old figure reads as a current one, which is worse
 * on a live tile than on a table. `failed` is what lets the strip say so.
 */
export const useTxnStats = (minutes = 60, topRules?: number) => {
	const { get } = useNetwork({ updateState: false });
	const [stats, setStats] = useState<ITxnStats | null>(null);
	const [failed, setFailed] = useState(false);

	const load = useCallback(async () => {
		/* `topRules` is only sent when a caller asks for it. The endpoint's own
		   default of 7 is what the triage deck's leaderboard wants; the rules page
		   joins these counts onto the WHOLE catalogue and needs all of them, or
		   every rule outside the top 7 reads as never having fired. */
		const top = topRules ? `&topRules=${topRules}` : '';
		const resp = await get(
			`${API_URL.TM_STATS}?minutes=${minutes}${top}`
		).catch(() => null);
		if (resp?.data) {
			setStats(resp.data);
			setFailed(false);
		} else {
			setFailed(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [minutes, topRules]);

	useEffect(() => {
		load();
	}, [load]);
	useVisiblePoll(load);

	return { stats, failed };
};

/** The newest transactions, for the incoming tape. */
export const useRecentTransactions = (pageSize = 12) => {
	const { get } = useNetwork({ updateState: false });
	const [rows, setRows] = useState<ITransaction[]>([]);

	const load = useCallback(async () => {
		const resp = await get(
			`${API_URL.TM_TRANSACTIONS}?pageSize=${pageSize}`
		).catch(() => null);
		if (resp?.data?.data) setRows(resp.data.data);
		// `get` is rebuilt by useNetwork on every render, so listing it here would
		// rebuild `load` every render and restart the poll each time.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageSize]);

	useEffect(() => {
		load();
	}, [load]);
	useVisiblePoll(load);

	return rows;
};

/** One transaction with its rule runs, activity and alerts — the drawer. */
export const useTransaction = (id: string | null) => {
	const { get } = useNetwork({ updateState: false });
	const [txn, setTxn] = useState<ITransaction | null>(null);

	const load = useCallback(async () => {
		if (!id) {
			setTxn(null);
			return;
		}
		const resp = await get(`${API_URL.TM_TRANSACTIONS}/${id}`).catch(
			() => null
		);
		if (resp?.data) setTxn(resp.data);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	useEffect(() => {
		load();
	}, [load]);

	return { txn, reload: load };
};

/**
 * The pre-filled SAR draft for one transaction.
 *
 * Built server-side from records on every read (libs/fincen/sar-prefill), so
 * it always reflects the current alert and case — nothing here caches it. It is
 * reloaded after the drawer's own writes (an escalation widens it to the case).
 *
 * `save` sends officer input for the subject (DOB, address, ID) and stores it
 * on the case — the draft never overwrites what a person typed.
 */
/* `get` returns the whole payload ({message, data}); `post` returns it ALREADY
   UNWRAPPED (useNetwork: `apiPayload.data ?? apiPayload`). Reading `.data` off a
   POST result is undefined — which left the SAR pop-out showing the old draft
   after "Link session" and made "Generate XML" produce nothing. Accept either. */
const asDraft = (resp: any): ISarDraft | null =>
	resp?.status && resp?.activity ? resp : resp?.data?.status && resp?.data?.activity ? resp.data : null;

export const useSarDraft = (txnId: string | null) => {
	const { get, post } = useNetwork({ updateState: false });
	const [draft, setDraft] = useState<ISarDraft | null>(null);
	const [loading, setLoading] = useState(false);

	const load = useCallback(async () => {
		if (!txnId) {
			setDraft(null);
			return;
		}
		setLoading(true);
		const resp = await get(`${API_URL.TM_SAR_DRAFTS}/${txnId}`).catch(() => null);
		setDraft(asDraft(resp));
		setLoading(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [txnId]);

	useEffect(() => {
		load();
	}, [load]);

	const save = useCallback(
		async (
			caseId: string,
			subject: Record<string, unknown>,
			extra: { narrative?: string; kycSessionId?: string | null } = {}
		) => {
			const resp = await post(API_URL.TM_SAR_DRAFTS, {
				caseId,
				subject,
				persist: true,
				...extra,
			}).catch(() => null);
			const d = asDraft(resp);
			if (d) setDraft(d);
			return d;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	/* The XML for review. Not persisted: a typed full SSN travels in this one
	   request and the server strips it before anything is saved. */
	const generateXml = useCallback(
		async (caseId: string, subject: Record<string, unknown>, narrative: string) => {
			const resp = await post(API_URL.TM_SAR_DRAFTS, { caseId, subject, narrative }).catch(() => null);
			return asDraft(resp);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	return { draft, loading, reload: load, save, generateXml };
};

/**
 * WHERE ONE CASE'S SAR STANDS — the case drawer's three reads, together.
 *
 *   - the draft: `POST /sar-drafts {caseId}` WITHOUT `persist`, which is a pure
 *     build and writes nothing (the GET form is keyed by transaction, not case);
 *   - the FinCEN submissions for the case, with the SDTM connection state;
 *   - the business's filer profile.
 *
 * Each is null when its read FAILED, which is deliberately different from an
 * empty answer: "no submissions" and "could not ask" must not look alike.
 * `get` returns null on any error for TM paths (no toast, see useNetwork), and
 * `post` unwraps `data` already — `asDraft` accepts either shape.
 */
export const useCaseSar = (caseId: string | null, businessId?: string | null) => {
	const { get, post } = useNetwork({ updateState: false });
	const [draft, setDraft] = useState<ISarDraft | null>(null);
	const [fincen, setFincen] = useState<{
		connection: IFincenConnection;
		submissions: IFincenSubmission[];
	} | null>(null);
	const [filer, setFiler] = useState<ISarFilerProfileResp | null>(null);
	const [loading, setLoading] = useState(false);


	const load = useCallback(async () => {
		setDraft(null);
		setFincen(null);
		setFiler(null);
		if (!caseId) return;
		setLoading(true);
		const biz = businessId ? `&businessId=${encodeURIComponent(businessId)}` : '';
		const [d, f, p] = await Promise.all([
			post(API_URL.TM_SAR_DRAFTS, { caseId, ...(businessId ? { businessId } : {}) }).catch(() => null),
			get(`${API_URL.TM_FINCEN_SUBMISSIONS}?caseId=${encodeURIComponent(caseId)}${biz}`).catch(() => null),
			get(`${API_URL.TM_SAR_FILER_PROFILES}${businessId ? `?businessId=${encodeURIComponent(businessId)}` : ''}`).catch(
				() => null
			),
		]);
		setDraft(asDraft(d));
		const fd = (f as any)?.data;
		setFincen(
			fd?.connection
				? {
						connection: fd.connection,
						/* Newest first. The service already sorts, but the order is
						   what the history MEANS, so it is not left to a remote default. */
						submissions: (Array.isArray(fd.submissions) ? fd.submissions : [])
							.slice()
							.sort(
								(a: IFincenSubmission, b: IFincenSubmission) =>
									new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime()
							),
					}
				: null
		);
		const pd = (p as any)?.data;
		setFiler(pd && typeof pd === 'object' && 'profile' in pd ? pd : null);
		setLoading(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [caseId, businessId]);

	useEffect(() => {
		load();
	}, [load]);

	return { draft, fincen, filer, loading, reload: load };
};

/**
 * Who a case can be assigned to — the same list the Sessions screen assigns
 * from (`/assignee-users`), so an analyst is one person across both screens.
 * Fetched once per drawer; the team does not change while a drawer is open.
 */
export const useAssignees = () => {
	const { get } = useNetwork({ updateState: false });
	const [assignees, setAssignees] = useState<IAssignee[]>([]);
	useEffect(() => {
		get(API_URL.ASSIGNEE_USERS)
			.then((r: any) => setAssignees(Array.isArray(r?.data) ? r.data : []))
			.catch(() => setAssignees([]));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	return assignees;
};

/**
 * Assign the CASE — its alert and, once escalated, the case itself — to one
 * person. `assignedTo` is a display name on both records, which is what the
 * queue's "assigned" column and "Assigned to me" tab read.
 */
export const useAssignCase = () => {
	const { patch } = useNetwork({ updateState: false });
	return useCallback(
		async (alertId: string | null, caseId: string | null, name: string) => {
			await Promise.all([
				alertId ? patch(`${API_URL.TM_ALERTS}/${alertId}`, { assignedTo: name }).catch(() => null) : null,
				caseId ? patch(`${API_URL.TM_CASES}/${caseId}`, { assignedTo: name }).catch(() => null) : null,
			]);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);
};

/**
 * The rule catalogue, and the writes that tune it.
 *
 * NOT polled. The catalogue changes only when an operator changes it, and this
 * hook is the only thing that changes it — so it reloads after its own writes
 * rather than on a timer. The queue polls because transactions arrive on their
 * own; rules do not.
 */
export const useMonitoringRules = () => {
	const { get, patch, remove: del } = useNetwork({ updateState: false });
	const [rules, setRules] = useState<IMonitoringRule[]>([]);
	const [loading, setLoading] = useState(true);
	const [failed, setFailed] = useState(false);

	const load = useCallback(async () => {
		const resp = await get(API_URL.TM_RULES).catch(() => null);
		if (Array.isArray(resp?.data)) {
			setRules(resp.data);
			setFailed(false);
		} else {
			setFailed(true);
		}
		setLoading(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	/**
	 * Tune one rule.
	 *
	 * The list is reloaded rather than patched in place because the service may
	 * answer with a DIFFERENT document than the one addressed: editing a shared
	 * default clones it to the tenant, so the row that comes back has a new `_id`
	 * and the old one must stop being the thing the next edit addresses.
	 */
	const tune = useCallback(
		async (ruleId: string, body: Record<string, unknown>) => {
			const res = await patch(`${API_URL.TM_RULES}/${ruleId}`, body);
			await load();
			return res;
		},
		[patch, load]
	);

	/**
	 * Delete this business's OVERRIDE of a rule, which resets it to the shared
	 * default. The service refuses this for a shared default itself — see its own
	 * `remove`; the UI does not offer it there either, so a rejection here means
	 * the list was stale, and the reload is what corrects it.
	 */
	const remove = useCallback(
		async (ruleId: string) => {
			const res = await del(`${API_URL.TM_RULES}/${ruleId}`);
			await load();
			return res;
		},
		[del, load]
	);

	return { rules, loading, failed, reload: load, tune, remove };
};

/**
 * The historical-import log, and the writes that drive it.
 *
 * POLLED ONLY WHILE SOMETHING IS RUNNING. An import log at rest never changes on
 * its own — nothing but this page creates a batch — so a permanent interval
 * would be load for nothing. But a run in progress does change, so the poll
 * turns itself on whenever any batch in view is queued/validating/importing and
 * off again when they all settle.
 */
export const useImportBatches = (filters: {
	status?: string;
	q?: string;
	pageNo?: number;
}) => {
	const { get, patch, remove: del } = useNetwork({ updateState: false });
	/* The upload is a hand-rolled XHR (see `upload`), so it needs the token the
	   same place useNetwork gets it rather than a second guess at where it lives. */
	const { accessToken } = useRecoilValue(loginState);
	const [batches, setBatches] = useState<IImportBatch[]>([]);
	const [progress, setProgress] = useState<IUploadProgress>({
		phase: 'idle',
		loaded: 0,
		total: 0,
		bytesPerSec: 0,
		etaSeconds: null,
	});
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(true);
	const [failed, setFailed] = useState(false);

	const load = useCallback(async () => {
		const qs = new URLSearchParams();
		if (filters.status && filters.status !== 'All')
			qs.set('status', filters.status);
		if (filters.q) qs.set('q', filters.q);
		qs.set('pageNo', String(filters.pageNo ?? 1));
		qs.set('pageSize', String(IMPORTS_PAGE_SIZE));
		const resp = await get(`${API_URL.TM_IMPORTS}?${qs.toString()}`).catch(
			() => null
		);
		if (resp?.data?.data) {
			setBatches(resp.data.data);
			setTotal(resp.data.total ?? 0);
			setFailed(false);
		} else {
			setFailed(true);
		}
		setLoading(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(filters)]);

	useEffect(() => {
		load();
	}, [load]);

	/* The gate: any batch still moving means keep looking. */
	const running = batches.some(b =>
		['queued', 'validating', 'importing'].includes(b.status)
	);
	useEffect(() => {
		if (!running) return undefined;
		const t = setInterval(() => load(), IMPORT_POLL_MS);
		return () => clearInterval(t);
	}, [running, load]);

	/**
	 * Start an import, reporting upload progress as it goes.
	 *
	 * XHR, NOT `fetch`. `fetch` cannot report request-upload progress — it exposes
	 * download progress only — and a 100 MB upload with no bar is several minutes
	 * of a client not knowing whether anything is happening. `XMLHttpRequest`'s
	 * `upload.onprogress` is still the only way to get this in a browser, so the
	 * one place in the panel that needs it uses it directly rather than through
	 * `useNetwork`. The token comes from the same `loginState` useNetwork reads.
	 *
	 * The whole CSV goes in one request because the server must hold every row
	 * before writing any of them — it sorts chronologically first, so chunking
	 * would break the ordering guarantee rather than help.
	 */
	const upload = useCallback(
		(label: string, filename: string, csv: string): Promise<any> =>
			new Promise((resolve, reject) => {
				const body = JSON.stringify({ label, filename, csv });
				const xhr = new XMLHttpRequest();
				xhr.open('POST', `${API_HOST}${API_URL.TM_IMPORTS}`, true);
				xhr.setRequestHeader('Content-Type', 'application/json');
				if (accessToken)
					xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);

				const startedAt = Date.now();
				/* Rate is measured over the WHOLE transfer so far rather than between
				   the last two events. A per-event rate on a fast local connection
				   swings wildly — the ETA would flicker between two seconds and two
				   minutes and be worse than none. A cumulative average settles. */
				xhr.upload.onprogress = e => {
					if (!e.lengthComputable) return;
					const secs = (Date.now() - startedAt) / 1000;
					const bytesPerSec = secs > 0 ? e.loaded / secs : 0;
					const remaining = e.total - e.loaded;
					setProgress({
						phase: 'uploading',
						loaded: e.loaded,
						total: e.total,
						bytesPerSec,
						/* Withheld until a second of real data exists: an ETA computed
						   off the first 40 ms of a transfer is a guess presented as a
						   number, and it is always wildly wrong. */
						etaSeconds:
							secs > 1 && bytesPerSec > 0 ? remaining / bytesPerSec : null,
					});
				};

				xhr.onload = () => {
					setProgress(p => ({ ...p, phase: 'done' }));
					let parsed: any = null;
					try {
						parsed = JSON.parse(xhr.responseText);
					} catch {
						parsed = null;
					}
					if (xhr.status >= 200 && xhr.status < 300) {
						load();
						resolve(parsed);
						return;
					}
					/* A 413 never reaches the service, so it has no message of its own —
					   nginx answers it. Named here, because "Request Entity Too Large" in
					   a toast tells a client nothing about which limit they hit. */
					if (xhr.status === 413) {
						reject(
							new Error(
								'The gateway refused the upload as too large. The limit is 100 MB per import — split the file, by date range is easiest.'
							)
						);
						return;
					}
					reject(
						new Error(
							parsed?.message ?? `The upload failed (HTTP ${xhr.status}).`
						)
					);
				};
				xhr.onerror = () => {
					setProgress(p => ({ ...p, phase: 'done' }));
					reject(new Error('The connection dropped during the upload.'));
				};
				xhr.onabort = () => {
					setProgress(p => ({ ...p, phase: 'done' }));
					reject(new Error('The upload was cancelled.'));
				};

				setProgress({
					phase: 'uploading',
					loaded: 0,
					total: body.length,
					bytesPerSec: 0,
					etaSeconds: null,
				});
				xhr.send(body);
			}),
		[accessToken, load]
	);

	const act = useCallback(
		async (batchId: string, body: Record<string, unknown>) => {
			const res = await patch(`${API_URL.TM_IMPORTS}/${batchId}`, body);
			await load();
			return res;
		},
		[patch, load]
	);

	const destroy = useCallback(
		async (batchId: string) => {
			const res = await del(`${API_URL.TM_IMPORTS}/${batchId}`);
			await load();
			return res;
		},
		[del, load]
	);

	/**
	 * The per-row reject report, as CSV text.
	 *
	 * Fetched through `useNetwork` like everything else rather than a raw
	 * `fetch`: the report is behind the same ownership check as the batch, and
	 * useNetwork is what knows where the token lives and how to attach it. It
	 * comes back inside the normal JSON envelope because a xitkov service method
	 * cannot set its own Content-Type, so the caller turns it into a file.
	 */
	const errorReport = useCallback(
		async (batchId: string) => {
			const resp = await get(
				`${API_URL.TM_IMPORTS}/${batchId}?report=errors`
			).catch(() => null);
			return (resp?.data ?? null) as {
				filename: string;
				csv: string;
				truncated: boolean;
				rowsRejected: number;
			} | null;
		},
		[get]
	);

	/**
	 * Apply a CSV of corrected rows to one import.
	 *
	 * A correction is a RE-SCORE, not an edit: correcting one row changes the
	 * correct score of every later row for that subject, so the service re-scores
	 * the affected tail. The result says what actually moved, which is the only
	 * way a correction is reviewable rather than trusted.
	 */
	const correct = useCallback(
		async (batchId: string, correctionCsv: string) => {
			const res = await patch(`${API_URL.TM_IMPORTS}/${batchId}`, {
				correctionCsv,
			});
			await load();
			return res;
		},
		[patch, load]
	);

	return {
		batches,
		total,
		loading,
		failed,
		reload: load,
		upload,
		progress,
		setProgress,
		act,
		destroy,
		errorReport,
		correct,
	};
};

/** One batch's detail, plus its findings. Polled while it is still running. */
export const useImportBatch = (batchId: string | null) => {
	const { get } = useNetwork({ updateState: false });
	const [batch, setBatch] = useState<IImportBatch | null>(null);

	const load = useCallback(async () => {
		if (!batchId) {
			setBatch(null);
			return;
		}
		const resp = await get(`${API_URL.TM_IMPORTS}/${batchId}`).catch(
			() => null
		);
		if (resp?.data) setBatch(resp.data);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [batchId]);

	useEffect(() => {
		load();
	}, [load]);

	const running =
		!!batch && ['queued', 'validating', 'importing'].includes(batch.status);
	useEffect(() => {
		if (!running) return undefined;
		const t = setInterval(() => load(), IMPORT_POLL_MS);
		return () => clearInterval(t);
	}, [running, load]);

	return { batch, reload: load };
};

/**
 * One batch's FINDINGS — the historical alerts it raised.
 *
 * Always scoped to a batch. There is no "every historical alert" request to
 * make: the service requires the batch id exactly as it requires a resolved
 * business, so that a read can never be shaped loosely enough to return another
 * client's back-book.
 */
export const useImportFindings = (batchId: string | null, live = false) => {
	const { get, patch } = useNetwork({ updateState: false });
	const [findings, setFindings] = useState<IAlert[]>([]);

	const load = useCallback(async () => {
		if (!batchId) {
			setFindings([]);
			return;
		}
		const resp = await get(
			`${API_URL.TM_ALERTS}?historicalBatchId=${encodeURIComponent(
				batchId
			)}&pageSize=200`
		).catch(() => null);
		setFindings(resp?.data?.data ?? []);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [batchId]);

	useEffect(() => {
		load();
	}, [load]);

	/**
	 * REFRESHED WHILE THE IMPORT IS STILL RUNNING, and once more when it stops.
	 *
	 * Without this the findings list was silently always empty on the path a
	 * client actually takes: the detail panel opens the instant the upload is
	 * accepted, findings are fetched once at that moment — before a single row
	 * has been scored — and nothing ever asked again. So an import that raised
	 * real findings rendered "no row in this import needed review" until the
	 * panel was closed and reopened. Caught by driving a real upload through the
	 * UI and comparing against the alert the service had definitely written.
	 *
	 * The final reload is separate from the interval on purpose: `live` flipping
	 * false tears the interval down, and the last rows are usually scored between
	 * its final tick and that teardown.
	 */
	useEffect(() => {
		if (!live) return undefined;
		const t = setInterval(() => load(), IMPORT_POLL_MS);
		return () => clearInterval(t);
	}, [live, load]);

	useEffect(() => {
		if (!live) load();
	}, [live, load]);

	/* Sends ONE finding to live triage. Per finding on purpose — a 50,000-row
	   import must not be able to seed 50,000 fresh SLA clocks in one click. */
	const promote = useCallback(
		async (alertId: string) => {
			const res = await patch(`${API_URL.TM_ALERTS}/${alertId}`, {
				promote: true,
			});
			await load();
			return res;
		},
		[patch, load]
	);

	/**
	 * Send several findings to triage at once, by severity.
	 *
	 * A severity floor is REQUIRED by the service — there is deliberately no
	 * "promote everything" — and it refuses rather than truncates past its cap,
	 * because promoting the first 500 of 3,000 would leave 2,500 the operator
	 * believes are live. The due dates are staggered so the queue arrives as work
	 * that can be paced rather than a wall that is instantly late.
	 */
	const promoteBulk = useCallback(
		async (batchId: string, severities: string[]) => {
			const anyFinding = findings[0];
			if (!anyFinding) return null;
			const res = await patch(`${API_URL.TM_ALERTS}/${anyFinding._id}`, {
				promoteBulk: true,
				importBatchId: batchId,
				severities,
			});
			await load();
			return res;
		},
		[patch, load, findings]
	);

	return { findings, reload: load, promote, promoteBulk };
};

/**
 * The FX table — the rates that convert a historical row whose own converted
 * figure is missing.
 *
 * Only ever consulted at import time, so this is not polled: it changes when an
 * operator changes it, and this hook is the only thing that changes it.
 */
export const useFxRates = () => {
	const { get, post, remove: del } = useNetwork({ updateState: false });
	const [rates, setRates] = useState<IFxRate[]>([]);
	const [pairs, setPairs] = useState<IFxPair[]>([]);
	const [loading, setLoading] = useState(true);

	const load = useCallback(async () => {
		const resp = await get(API_URL.TM_FX_RATES).catch(() => null);
		setRates(resp?.data?.data ?? []);
		setPairs(resp?.data?.pairs ?? []);
		setLoading(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	/** One rate, or a whole treasury CSV — the service takes either. */
	const addRates = useCallback(
		async (body: Record<string, unknown>) => {
			const res = await post(API_URL.TM_FX_RATES, body);
			await load();
			return res;
		},
		[post, load]
	);

	const removeRate = useCallback(
		async (id: string) => {
			const res = await del(`${API_URL.TM_FX_RATES}/${id}`);
			await load();
			return res;
		},
		[del, load]
	);

	return { rates, pairs, loading, reload: load, addRates, removeRate };
};

/** Cases for one subject, and the writes that work them. */
export const useCases = (subject: string | null) => {
	const { get, post, patch } = useNetwork({ updateState: false });
	const [cases, setCases] = useState<any[]>([]);

	const load = useCallback(async () => {
		if (!subject) {
			setCases([]);
			return;
		}
		const resp = await get(
			`${API_URL.TM_CASES}?subject=${encodeURIComponent(subject)}`
		).catch(() => null);
		setCases(Array.isArray(resp?.data) ? resp.data : []);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [subject]);

	useEffect(() => {
		load();
	}, [load]);

	const openCase = useCallback(
		async (title: string) => {
			await post(API_URL.TM_CASES, {
				title,
				subjects: subject ? [subject] : [],
			});
			await load();
		},
		[post, subject, load]
	);

	const updateCase = useCallback(
		async (caseId: string, body: Record<string, unknown>) => {
			const res = await patch(`${API_URL.TM_CASES}/${caseId}`, body);
			await load();
			return res;
		},
		[patch, load]
	);

	return { cases, reload: load, openCase, updateCase };
};

/** The analyst decisions. Each is a PATCH the service records on the activity. */
export const useTmActions = () => {
	const { patch } = useNetwork({ updateState: false });

	const decide = useCallback(
		(txnId: string, status: string, reasonCode?: string) =>
			patch(`${API_URL.TM_TRANSACTIONS}/${txnId}`, { status, reasonCode }),
		[patch]
	);

	const updateAlert = useCallback(
		(alertId: string, body: Record<string, unknown>) =>
			patch(`${API_URL.TM_ALERTS}/${alertId}`, body),
		[patch]
	);

	/* Bulk actions run SEQUENTIALLY, not through Promise.all. Each decision is a
	   separate write the service records on its own activity trail, and firing
	   forty at once at a shared cluster is how one impatient click turns into a
	   burst that times out half of itself — leaving the analyst unable to tell
	   which rows actually went through. Slower, and every row's outcome is known. */
	const bulk = useCallback(
		async (ids: string[], run: (id: string) => Promise<unknown>) => {
			const failed: string[] = [];
			for (const id of ids) {
				try {
					await run(id);
				} catch {
					failed.push(id);
				}
			}
			return failed;
		},
		[]
	);

	return { decide, updateAlert, bulk };
};

/** The three formats a transaction record is issued in. */
export const RECORD_FORMATS: { label: string; value: 'pdf' | 'csv' | 'xlsx' }[] = [
	{ label: 'PDF (.pdf)', value: 'pdf' },
	{ label: 'Spreadsheet (.xlsx)', value: 'xlsx' },
	{ label: 'CSV (.csv)', value: 'csv' },
];

/**
 * Downloading the transaction record.
 *
 * The service assembles, renders, stores and answers with a signed url, so this
 * hook's whole job is to ask, wait visibly, and open what comes back — the same
 * shape `useSessionDetailsActions.exportPdf` handles for session reports, and
 * deliberately the same so the two downloads behave identically.
 *
 * IT TAKES TIME AND SAYS SO. A record is four reads, a Puppeteer render and an
 * upload; on a cold render service that is comfortably ten seconds of a button
 * that looks like it did nothing. `toast.promise` is what the sessions download
 * uses for exactly this reason.
 *
 * `window.open(url, '_self')` rather than a new tab: a popup blocker silently
 * swallows a `window.open` that is not a direct result of a click, and by the
 * time the url arrives the click is long over. Navigating the current tab to a
 * Content-Disposition: attachment url downloads without navigating away.
 */
export const useTransactionReport = () => {
	const { post } = useNetwork({ updateState: false });
	const [busy, setBusy] = useState(false);

	const download = useCallback(
		async (transactionIds: string[], fileType: 'pdf' | 'csv' | 'xlsx') => {
			if (!transactionIds.length) return;
			setBusy(true);
			try {
				const res = await toast.promise(
					post(API_URL.TRANSACTION_REPORTS, { transactionIds, fileType }),
					{
						pending:
							transactionIds.length > 1
								? `Building the record for ${transactionIds.length} transactions…`
								: 'Building the transaction record…',
					}
				);
				const url: string | undefined = res?.url?.[0];
				if (url) {
					window.open(url, '_self');
					return;
				}
				/* The service refuses with a REASON — an unconfigured render
				   service, a transaction that is not this tenant's, a selection
				   past the cap — and that reason is the only thing that tells the
				   analyst what to do next. Showing it beats a generic failure. */
				toast.error(
					res?.message ??
						'The record could not be produced, and the service gave no reason.'
				);
			} catch (error: any) {
				toast.error(
					error?.response?.data?.message ??
						error?.message ??
						'The record could not be produced.'
				);
			} finally {
				setBusy(false);
			}
		},
		[post]
	);

	return { download, busy };
};

/**
 * Country coverage — the whole list in one response.
 *
 * No paging: it is a settings screen of ~240 rows that the user searches and
 * toggles, and a paged list would make the tab counts and any bulk action mean
 * "…on this page". Filtering happens in the component.
 */
export interface ICountryRow {
	code: string;
	name: string;
	enabled: boolean;
	locked: boolean;
	program: string | null;
	citation: string | null;
	riskTier: 'high' | 'medium' | 'low';
	riskScore: number;
	/** True when this business has pinned its own tier/score for this country. */
	overridden: boolean;
	defaultTier: 'high' | 'medium' | 'low';
	defaultScore: number;
	riskNote: string | null;
}

export interface ICountryCounts {
	total: number;
	locked: number;
	disabled: number;
	enabled: number;
	high: number;
	medium: number;
	low: number;
}

export const useCountryCoverage = () => {
	const { get, patch } = useNetwork({ updateState: false });
	const [rows, setRows] = useState<ICountryRow[]>([]);
	const [counts, setCounts] = useState<ICountryCounts>({
		total: 0,
		locked: 0,
		disabled: 0,
		enabled: 0,
		high: 0,
		medium: 0,
		low: 0,
	});
	const [tierDefaults, setTierDefaults] = useState<Record<string, number>>({
		high: 30,
		medium: 15,
		low: 0,
	});
	const [regions, setRegions] = useState<string[]>([]);
	const [loading, setLoading] = useState(true);
	const [failed, setFailed] = useState(false);

	const reload = useCallback(async () => {
		const resp = await get(API_URL.TM_COUNTRY_COVERAGE).catch(() => null);
		const d = resp?.data;
		if (!d?.data) {
			setFailed(true);
			setLoading(false);
			return;
		}
		setRows(d.data);
		setCounts(
			d.counts ?? {
				total: 0,
				locked: 0,
				disabled: 0,
				enabled: 0,
				high: 0,
				medium: 0,
				low: 0,
			}
		);
		setTierDefaults(d.tierDefaults ?? { high: 30, medium: 15, low: 0 });
		setRegions(d.unenforceableRegions ?? []);
		setFailed(false);
		setLoading(false);
	}, [get]);

	/**
	 * Change a country's rating. `null` for either field REVERTS it to the
	 * platform default — which is not the same as writing today's default
	 * numbers, because a reverted row keeps following FATF as the lists change.
	 */
	const setRisk = useCallback(
		async (
			code: string,
			riskTier: 'high' | 'medium' | 'low' | null,
			riskScore: number | null
		): Promise<{ error?: string }> => {
			const res = await patch(`${API_URL.TM_COUNTRY_COVERAGE}/${code}`, {
				riskTier,
				riskScore,
			});
			await reload();
			return res?.data
				? {}
				: { error: res?.message ?? 'That rating was not saved.' };
		},
		[patch, reload]
	);

	const setEnabled = useCallback(
		async (code: string, enabled: boolean): Promise<{ error?: string }> => {
			/* Optimistic, then reconciled from the server's own answer. The server
			   refuses an embargoed country even though the switch is locked here —
			   a disabled control stops nobody with a terminal — so a refusal must
			   put the row back rather than leave the screen claiming a change that
			   did not happen. */
			setRows(prev => prev.map(r => (r.code === code ? { ...r, enabled } : r)));
			const res = await patch(`${API_URL.TM_COUNTRY_COVERAGE}/${code}`, {
				enabled,
			});
			if (!res?.data) {
				setRows(prev =>
					prev.map(r => (r.code === code ? { ...r, enabled: !enabled } : r))
				);
				await reload();
				return {
					error:
						res?.message ??
						'That change was not applied. The list has been refreshed from the server.',
				};
			}
			await reload();
			return {};
		},
		[patch, reload]
	);

	return {
		rows,
		counts,
		tierDefaults,
		regions,
		loading,
		failed,
		setEnabled,
		setRisk,
		reload,
	};
};

/**
 * The data-feeds page.
 *
 * One request carries the feed, its health, the recent runs, the alert
 * recipients and the LIVE scheduler state — they are read together and shown
 * together, and splitting them would let the page render a health badge next to
 * a schedule fetched a moment apart.
 */
export const useDataFeeds = () => {
	const { get, post, patch } = useNetwork({ updateState: false });
	const [feed, setFeed] = useState<any>(null);
	const [health, setHealth] = useState<any>(null);
	const [runs, setRuns] = useState<any[]>([]);
	const [recipients, setRecipients] = useState<any[]>([]);
	const [scheduler, setScheduler] = useState<any>(null);
	const [pulls, setPulls] = useState<any[]>([]);
	const [coverage, setCoverage] = useState<any>(null);
	const [alerting, setAlerting] = useState<any>(null);
	const [loading, setLoading] = useState(true);
	const [failed, setFailed] = useState(false);

	const reload = useCallback(async () => {
		const resp = await get(API_URL.TM_DATA_FEEDS).catch(() => null);
		const d = resp?.data;
		if (!d) {
			setFailed(true);
			setLoading(false);
			return;
		}
		setFeed(d.feed ?? null);
		setHealth(d.health ?? null);
		setRuns(Array.isArray(d.runs) ? d.runs : []);
		setRecipients(Array.isArray(d.recipients) ? d.recipients : []);
		setScheduler(d.scheduler ?? null);
		setPulls(Array.isArray(d.pulls) ? d.pulls : []);
		setCoverage(d.coverage ?? null);
		setAlerting(d.alerting ?? null);
		setFailed(false);
		setLoading(false);
	}, [get]);

	/* A real collection is a 28MB fetch and takes ~20s. Awaited rather than
	   fired and forgotten, because a "collect now" button that cannot tell you
	   what happened is worse than no button. */
	const runNow = useCallback(async () => {
		const res = await post(API_URL.TM_DATA_FEEDS, { action: 'run-now' }).catch(
			() => null
		);
		await reload();
		return (
			res?.data ?? {
				ok: false,
				failureReason: res?.message ?? 'the request did not complete',
			}
		);
	}, [post, reload]);

	const testNotify = useCallback(async () => {
		const res = await post(API_URL.TM_DATA_FEEDS, {
			action: 'test-notification',
		}).catch(() => null);
		return (
			res?.data ?? {
				canNotify: false,
				reason: res?.message ?? 'the test could not be sent',
				recipients: [],
			}
		);
	}, [post]);

	const addRecipient = useCallback(
		async (email: string) => {
			const res = await patch(`${API_URL.TM_DATA_FEEDS}/add-recipient`, {
				email,
			});
			await reload();
			return res?.data
				? {}
				: { error: res?.message ?? 'that address was not added' };
		},
		[patch, reload]
	);

	const removeRecipient = useCallback(
		async (email: string) => {
			await patch(`${API_URL.TM_DATA_FEEDS}/remove-recipient`, { email });
			await reload();
		},
		[patch, reload]
	);

	return {
		feed,
		health,
		runs,
		recipients,
		scheduler,
		pulls,
		coverage,
		alerting,
		loading,
		failed,
		reload,
		runNow,
		testNotify,
		addRecipient,
		removeRecipient,
	};
};
